/**
 * The financial years a gift can still count in, worked out from today (owner, 24 Oct 2026: "the years
 * should update as the app gets older rather than hard coded"). A gift above the free area is counted for
 * five years from the day it was made (Services Australia), so the years that matter run from the one
 * holding the date five years ago up to this one, so far. The list moves on by itself each year.
 *
 * Until now the screen offered "FY ending June {this calendar year}" and the four before it: in
 * October 2026 that left out this financial year (July 2026 on) and the oldest year still counting
 * (from October 2021).
 */

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export interface GiftYear {
    /** The year the financial year ends in: 2027 for July 2026 – June 2027. The profile's key is `year${fyEnd}`. */
    fyEnd: number;
    /** "July 2026 to now", "2025–26", "2021–22, from October 2021". */
    label: string;
    /** The months of this financial year a gift could fall in and still count, oldest first, as 1–12. */
    months: Array<{ month: number; label: string }>;
}

const fyEndOf = (d: Date) => (d.getMonth() >= 6 ? d.getFullYear() + 1 : d.getFullYear());

export function giftYears(today: Date = new Date()): GiftYear[] {
    const fiveYearsAgo = new Date(today.getFullYear() - 5, today.getMonth(), 1);
    const newest = fyEndOf(today);
    const oldest = fyEndOf(fiveYearsAgo);
    const out: GiftYear[] = [];
    for (let fyEnd = newest; fyEnd >= oldest; fyEnd--) {
        // July (fyEnd − 1) … June fyEnd, cut to the window.
        const months: GiftYear['months'] = [];
        for (let i = 0; i < 12; i++) {
            const m = (6 + i) % 12; // 0-based: July first
            const y = m >= 6 ? fyEnd - 1 : fyEnd;
            const first = new Date(y, m, 1);
            if (first < fiveYearsAgo || first > today) continue;
            months.push({ month: m + 1, label: `${MONTHS[m]} ${y}` });
        }
        const short = `${fyEnd - 1}–${String(fyEnd).slice(-2)}`;
        const label = fyEnd === newest
            ? `July ${fyEnd - 1} to now`
            : fyEnd === oldest && months.length < 12 ? `${short}, from ${months[0]?.label ?? ''}` : short;
        out.push({ fyEnd, label, months });
    }
    return out;
}
